import { computed, watch, type Ref } from "vue";
import type { RouteLocationNormalized } from "vue-router";
import type { DateRangeSelection } from "@/services/data_filters";
import { useDataFiltersStore } from "@/store/data_filters";
import type { DashboardTab } from "@/types/dashboardTabs";

interface SharedDateFilterSource {
  pk: number;
  sharedDateFilter?: DateRangeSelection;
  tabs?: DashboardTab[];
}

const DASHBOARD_ROUTE_NAMES = new Set(["edit-dashboard", "view-dashboard"]);

// Module scope so it survives edit ↔ view navigation, which remounts the view.
let lastApplied: { pk: number; key: string } | null = null;

export function applySharedDateFilter(
  dashboardPk: number,
  filter: DateRangeSelection,
): void {
  const key = JSON.stringify(filter);
  if (lastApplied?.pk === dashboardPk && lastApplied.key === key) return;
  lastApplied = { pk: dashboardPk, key };
  useDataFiltersStore().applySavedTemporalFilter(filter);
}

export function useSharedDateFilter(
  dashboard: Ref<SharedDateFilterSource | undefined>,
  currentTab: Ref<number>,
  isSuppressed: () => boolean = () => false,
): void {
  const currentTabLocked = computed(() =>
    Boolean(dashboard.value?.tabs?.[currentTab.value]?.lockedDate),
  );
  // Also re-runs when the viewer leaves a locked tab, the first point the saved range can apply.
  watch(
    [dashboard, currentTabLocked],
    ([data, locked]) => {
      if (!data?.pk || !data.sharedDateFilter || locked || isSuppressed()) {
        return;
      }
      applySharedDateFilter(data.pk, data.sharedDateFilter);
    },
    { immediate: true },
  );
}

// A range picked elsewhere must not outlive the trip, or returning here would keep it.
export function releaseSharedDateFilterLatch(
  to: RouteLocationNormalized,
): void {
  const staysOnDashboard =
    DASHBOARD_ROUTE_NAMES.has(String(to.name)) &&
    Number(to.params.pk) === lastApplied?.pk;
  if (!staysOnDashboard) lastApplied = null;
}
